/**
 * Utility functions for generating meaningful history item display labels.
 * Prioritizes generated note content over raw input for easier identification.
 */

/**
 * Extract a meaningful title from a history item.
 * Priority: clinical_note A/P line > clinical_note HPI > ICD-10 descriptions > chief_complaint > original_notes snippet
 */
export const getHistoryDisplayTitle = (item) => {
  if (!item) return 'Untitled';

  // 1. Try to extract from clinical note A/P summary (most specific)
  if (item.clinical_note) {
    // Look for A/P diagnoses like "1. Low back pain w/ left-sided sciatica"
    const apMatch = item.clinical_note.match(/A\/P\s+SUMMARY:?\s*\n\s*1\.\s*(.+?)(?:\s*\(ICD-10|\n)/i);
    if (apMatch && apMatch[1]) {
      const title = apMatch[1].trim().replace(/\s+/g, ' ');
      if (title.length > 3 && title.length <= 80) return title;
      if (title.length > 80) return title.substring(0, 77) + '...';
    }

    // Look for HPI first line
    const hpiMatch = item.clinical_note.match(/HPI:?\s*\n?\s*(.+?)(?:\n|$)/i);
    if (hpiMatch && hpiMatch[1]) {
      const hpi = hpiMatch[1].trim().replace(/\s+/g, ' ');
      if (hpi.length > 5 && hpi.length <= 80) return hpi;
      if (hpi.length > 80) return hpi.substring(0, 77) + '...';
    }
  }

  // 2. Try admin letter - extract the reason/condition
  if (item.admin_letter && !item.clinical_note) {
    // Look for condition mentions in admin letters
    const condMatch = item.admin_letter.match(/(?:due to|based on|related to|for)\s+(?:a\s+|an\s+)?(?:medical condition[s]?\s+)?(?:affecting|related to|including)?\s*([^.,\n]{5,60})/i);
    if (condMatch && condMatch[1]) {
      const cond = condMatch[1].trim();
      if (cond.length > 3) return `Letter: ${cond}`;
    }
    // Fall back to letter type
    if (item.letter_type) {
      return `${getLetterLabel(item.letter_type)} Letter`;
    }
  }

  // 3. Try ICD-10 descriptions (provides clinical context)
  if (item.icd10_suggestions?.length > 0) {
    const primaryDx = item.icd10_suggestions.find(s =>
      s.relevance?.toLowerCase().includes('primary') ||
      s.relevance?.toLowerCase().includes('chief complaint')
    ) || item.icd10_suggestions[0];
    
    if (primaryDx?.description && primaryDx.description !== 'Illness, unspecified') {
      return primaryDx.description;
    }
  }

  // 4. Chief complaint
  if (item.chief_complaint?.trim()) {
    return item.chief_complaint.trim();
  }

  // 5. Original notes snippet (last resort)
  if (item.original_notes?.trim()) {
    const snippet = item.original_notes.trim().substring(0, 60);
    return snippet + (item.original_notes.length > 60 ? '...' : '');
  }

  return 'Untitled Note';
};

/**
 * Get the document type label for a history item.
 * Optional `role` parameter: when 'patient', provider-facing labels
 * ("Clinical Note", "Note + Letter") are renamed to "Letter/Message"
 * since patients see their own letters/portal messages, not SOAP notes.
 */
export const getDocTypeLabel = (item, role = null) => {
  if (item.is_draft) return 'Draft';
  if (item.letter_type) return getLetterLabel(item.letter_type);
  if (role === 'patient') {
    // Patients don't see provider-side clinical notes — they see letters/messages
    if (item.admin_letter || item.clinical_note) return 'Letter/Message';
    return 'Message';
  }
  if (item.admin_letter && item.clinical_note) return 'Note + Letter';
  if (item.admin_letter) return 'Letter';
  if (item.clinical_note) return 'Clinical Note';
  return 'Indication';
};

/**
 * Format letter type to readable label
 */
const getLetterLabel = (letterType) => {
  const labels = {
    'ada_accommodations': 'ADA',
    'fmla': 'FMLA',
    'deployment_deletion': 'Deploy Delete',
    'pcs_stabilization': 'PCS Stab',
    'profile_conversion': 'Profile Conv',
    'duty_limitations': 'Duty Limits',
    'fit_for_duty': 'Fit for Duty',
    'meb_narsum': 'MEB/NARSUM',
    'custom': 'Custom Letter',
  };
  return labels[letterType] || letterType?.replace(/_/g, ' ') || 'Letter';
};

/**
 * Format time for history display - prioritizes appointment time label over creation timestamp
 */
export const formatHistoryTime = (timestamp, patientLabel) => {
  // If patient_label contains a time like "10:30 AM", use that instead
  if (patientLabel) {
    const timeMatch = patientLabel.match(/\d{1,2}:\d{2}\s*(?:AM|PM)/i);
    if (timeMatch) return timeMatch[0];
    // If it's a custom label, return it as-is
    return patientLabel;
  }
  if (!timestamp) return '';
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Relative time formatter for history dock entries (Feb 27, 2026).
 * Returns a human-friendly string for recent entries:
 *   • <5s:    "just now"
 *   • <60s:   "30s ago"
 *   • <60m:   "12 min ago"
 *   • <24h:   "4h ago"
 *   • ≥24h:   null  (caller falls back to absolute date/time)
 * The 24h cutoff means today's charts get the at-a-glance "X ago" cue,
 * and older charts keep the full date/time so you can still locate them
 * by clock. Returns null past 24h so the caller can render absolute time.
 */
export const formatRelativeHistoryTime = (timestamp) => {
  if (!timestamp) return null;
  const ts = new Date(timestamp).getTime();
  if (isNaN(ts)) return null;
  const sec = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return null; // older than 24h — caller renders absolute time
};

/**
 * Format date for history grouping headers
 */
export const formatHistoryDate = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';

  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};
